import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import {
  CheckCircle, Package, Truck, MapPin, Phone, Mail,
  Calendar, Clock, Users, Award, Star, Heart, ArrowRight
} from 'lucide-react';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function OrderSuccessPage() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!order);

  useEffect(() => {
    // Trigger confetti animation
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return clearInterval(interval);
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch order if not in state
        if (!order && orderId) {
          const orderRes = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
          if (orderRes.ok) {
            setOrder(await orderRes.json());
          }
        }

        // Fetch company stats
        const statsRes = await fetch(`${BACKEND_URL}/api/company-stats`);
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, order]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              🎉 Order Placed Successfully!
            </h1>
            <p className="text-xl text-gray-600">
              Thank you for choosing <span className="text-blue-600 font-semibold">RO Dekho</span>!
            </p>
          </div>

          {/* Order Details Card */}
          {order && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b">
                <div>
                  <p className="text-sm text-gray-500">Order Number</p>
                  <p className="text-2xl font-bold text-gray-900">{order.order_number || order.id}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Order Total</p>
                  <p className="text-2xl font-bold text-green-600">₹{order.total?.toLocaleString()}</p>
                </div>
              </div>

              {/* Order Status */}
              <div className="mb-8">
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Order Confirmed</p>
                    <p className="text-sm text-gray-600">
                      We'll notify you when your order is shipped
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Items Ordered</h3>
                <div className="space-y-4">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded-lg bg-white"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-gray-900">₹{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Address */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <MapPin className="w-5 h-5" />
                    <span className="font-medium">Delivery Address</span>
                  </div>
                  <p className="text-gray-900">{order.full_name}</p>
                  <p className="text-gray-600">{order.address}</p>
                  <p className="text-gray-600">{order.city}, {order.state} - {order.pincode}</p>
                  <p className="text-gray-600">{order.phone}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Truck className="w-5 h-5" />
                    <span className="font-medium">Delivery & Payment</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">2-3 Business Days</p>
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-700 flex items-center gap-1.5">
                      <CheckCircle size={16} /> Cash on Delivery
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">Pay Rs.{order.total?.toLocaleString()} at delivery</p>
                  </div>
                </div>
              </div>

              {/* Track Order Link */}
              <div className="mt-6 text-center">
                <Link to={`/track/order/${order.id}`} className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors" data-testid="track-order-link">
                  <Truck size={18} /> Track Your Order
                </Link>
              </div>
            </div>
          )}

          {/* Company Stats - Trust Builders */}
          {stats && (
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 text-white mb-8">
              <h3 className="text-xl font-semibold text-center mb-6">
                You're in Good Hands! 🤝
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.years_experience}+</p>
                  <p className="text-sm text-blue-200">Years Experience</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.happy_customers?.toLocaleString()}+</p>
                  <p className="text-sm text-blue-200">Happy Customers</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.products_delivered?.toLocaleString()}+</p>
                  <p className="text-sm text-blue-200">Products Delivered</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6" />
                  </div>
                  <p className="text-3xl font-bold">{stats.satisfaction_rate}%</p>
                  <p className="text-sm text-blue-200">Satisfaction Rate</p>
                </div>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">What Happens Next?</h3>
            <div className="space-y-4">
              {[
                { icon: CheckCircle, title: 'Order Confirmed', desc: 'We\'ve received your order', done: true },
                { icon: Package, title: 'Processing', desc: 'We\'re preparing your order', done: false },
                { icon: Truck, title: 'Shipping', desc: 'Your order is on the way', done: false },
                { icon: Heart, title: 'Delivered', desc: 'Enjoy your pure water!', done: false }
              ].map((step, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step.done ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <step.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${step.done ? 'text-gray-900' : 'text-gray-400'}`}>{step.title}</p>
                    <p className="text-sm text-gray-500">{step.desc}</p>
                  </div>
                  {step.done && <CheckCircle className="w-5 h-5 text-green-600" />}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/account?tab=orders"
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
            >
              Track Your Order
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/products"
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>

          {/* Support */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Need help with your order?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+919887068888"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Phone className="w-5 h-5" />
                +91 98765-43210
              </a>
              <a
                href="mailto:support@rodekho.com"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Mail className="w-5 h-5" />
                support@rodekho.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default OrderSuccessPage;
